import React, { useState } from 'react';
import { Button, Modal, Text, BlockStack, InlineStack, Badge, Tooltip } from '@shopify/polaris';
import { ChatMajor } from '@shopify/polaris-icons';
import { 
  ORDER_STATUS, 
  MANUAL_ACTION_TYPE, 
  MANUAL_ACTION_AVAILABILITY,
  MANUAL_ACTION_PERMISSIONS 
} from '../../constants/orderConstants';
import { authService } from '../../services/authService';

const ManualWhatsAppButton = ({ 
  order,
  orderItem, 
  onWhatsAppSent, 
  userPermissions = [],
  disabled = false,
  size = 'medium',
  variant = 'primary'
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  
  // Check if order status allows WhatsApp sending
  const isStatusAllowed = MANUAL_ACTION_AVAILABILITY[MANUAL_ACTION_TYPE.WHATSAPP_SEND].includes(order?.status);
  
  // Check if order has customer phone number
  const hasPhoneNumber = order?.customer?.phone && order.customer.phone.trim() !== '';
  

  // Determine if button should be shown and enabled
  const shouldShow = isStatusAllowed && hasPhoneNumber;
  const isButtonDisabled = disabled || isSending;

  const handleSendWhatsApp = async () => {
    if (!order?.order_id || !orderItem?.id) return;

    setIsSending(true);
    try {
      // Call the API to send WhatsApp for specific order item
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/trials/${order.order_id}/items/${orderItem.id}/send-whatsapp`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          manual: true,
          timestamp: new Date().toISOString(),
          userId: localStorage.getItem('userId') || 'unknown'
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Success callback
        if (onWhatsAppSent) {
          onWhatsAppSent({
            orderId: order.id,
            orderItemId: orderItem.id,
            previousStatus: result.data.previousStatus,
            newStatus: result.data.newStatus,
            sentAt: result.data.whatsappSentAt,
            sentBy: result.data.sentBy
          });
        }
        
        setIsModalOpen(false);
      } else {
        throw new Error(result.message || 'Failed to send WhatsApp');
      }
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      // You might want to show a toast notification here
      alert(`Error sending WhatsApp: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  if (!shouldShow) {
    return null;
  }

  const getButtonContent = () => {
    if (isSending) return 'Sending...';
    return 'Send WhatsApp';
  };

  const getButtonTone = () => {
    return variant;
  };

  return (
    <>
      <Tooltip content={isSending ? 'Sending WhatsApp...' : 'Send WhatsApp'}>
        <Button
          variant="tertiary"
          size="slim"
          loading={isSending}
          disabled={isButtonDisabled}
          onClick={() => setIsModalOpen(true)}
          icon={ChatMajor}
        />
      </Tooltip>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          <div style={{ padding: '16px' }}>
            <Text variant="headingMd" as="h2">
              Send WhatsApp Notification
            </Text>
          </div>
        }
        primaryAction={{
          content: 'Send WhatsApp',
          onAction: handleSendWhatsApp,
          loading: isSending,
          variant: 'primary'
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setIsModalOpen(false)
          }
        ]}
        footer={<div style={{ padding: '16px' }} />}
      >
        <BlockStack gap="400">
          <Text as="p" variant="bodyMd">
            Are you sure you want to manually send a WhatsApp notification for this order item?
          </Text>
          
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Order Item Details:
            </Text>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Order ID:</Text>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {order?.order_id}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Product:</Text>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {orderItem?.name || 'N/A'}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Item ID:</Text>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {orderItem?.sku || orderItem?.id}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Customer:</Text>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {order?.customer?.name}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Phone:</Text>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {order?.customer?.phone}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="start">
              <Text as="span" variant="bodyMd">Order Status:</Text>
              <Badge tone="info">
                {order?.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Badge>
            </InlineStack>
          </BlockStack>

          <Text as="p" variant="bodyMd" tone="subdued">
            This will send a WhatsApp message to the customer with the preview images for this specific item.
          </Text>
        </BlockStack>
      </Modal>
    </>
  );
};

export default ManualWhatsAppButton;